import React, { useMemo, useState, useCallback } from 'react';
import { ActivityItem } from '../ActivityItem';
import { ActivityDayGroup } from './ActivityDayGroup';
import type { ActivityItemViewModel } from '../ActivityItem';
import type { ActivityDayGroup as ActivityDayGroupType } from '../../utils/activityAdapters';
import {
  groupActivitiesByDay,
  filterActivitiesByType,
  searchActivities,
} from '../../utils/activityAdapters';
import type { CommunicationType } from '../../types/tracker.types';
import './ActivityFeed.styles.css';

interface ActivityFeedProps {
  /** All activities to display */
  activities: ActivityItemViewModel[];

  /** Set of enabled communication types to show */
  enabledTypes: Set<CommunicationType>;

  /** Search query for filtering */
  searchQuery?: string;

  /** IDs of highlighted items (e.g., "type:id" for À faire justification) */
  highlightedIds?: Set<string>;

  /** Current user ID for permission checks */
  currentUserId?: string;

  /** Loading state */
  isLoading?: boolean;

  /** Callback when mark done is clicked */
  onMarkDone?: (type: CommunicationType, id: string, done: boolean, comment?: string) => void;

  /** Callback for edit action */
  onEdit?: (id: string, content: string, urgency?: string | null, assignedTo?: string | null) => void;

  /** Callback for delete action */
  onDelete?: (id: string) => void;

  /** Callback to view full email content */
  onViewEmailFull?: (id: string) => void;

  /** Callback to reallocate email */
  onReallocateEmail?: (id: string) => void;

  /** Callback to reallocate SMS */
  onReallocateSms?: (id: string) => void;

  /** Callback to reply to email */
  onReplyEmail?: (id: string) => void;

  /** Empty state message */
  emptyMessage?: string;
}

/**
 * ActivityFeed Component
 *
 * Displays a list of activities grouped by day with filtering support.
 * Uses the unified ActivityItem component for consistent rendering.
 */
export const ActivityFeed: React.FC<ActivityFeedProps> = ({
  activities,
  enabledTypes,
  searchQuery = '',
  highlightedIds = new Set(),
  currentUserId,
  isLoading,
  onMarkDone,
  onEdit,
  onDelete,
  onViewEmailFull,
  onReallocateEmail,
  onReallocateSms,
  onReplyEmail,
  emptyMessage = "Aucune activité à afficher",
}) => {
  // Track expanded items
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Toggle item expansion
  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Filter and group activities
  const groupedActivities = useMemo<ActivityDayGroupType[]>(() => {
    // First filter by type
    let filtered = filterActivitiesByType(activities, enabledTypes);

    // Then filter by search query
    if (searchQuery.trim()) {
      filtered = searchActivities(filtered, searchQuery);
    }

    // Group by day
    return groupActivitiesByDay(filtered);
  }, [activities, enabledTypes, searchQuery]);

  // Total filtered count
  const totalCount = useMemo(() => {
    return groupedActivities.reduce((sum, group) => sum + group.activities.length, 0);
  }, [groupedActivities]);

  // Loading state
  if (isLoading) {
    return (
      <div className="activity-feed activity-feed-loading">
        <div className="activity-feed-loading-spinner"></div>
        <span>Chargement des activités...</span>
      </div>
    );
  }

  // Empty state
  if (totalCount === 0) {
    return (
      <div className="activity-feed activity-feed-empty">
        <div className="activity-feed-empty-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 0 1-.825-.242m9.345-8.334a2.126 2.126 0 0 0-.476-.095 48.64 48.64 0 0 0-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0 0 11.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155"
            />
          </svg>
        </div>
        <p className="activity-feed-empty-text">{emptyMessage}</p>
        {searchQuery && (
          <p className="activity-feed-empty-hint">
            Essayez de modifier votre recherche ou vos filtres
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="activity-feed">
      {groupedActivities.map((group) => (
        <ActivityDayGroup key={group.label} group={group}>
          {group.activities.map((activity) => {
            const highlightKey = `${activity.type}:${activity.id}`;
            const isHighlighted = highlightedIds.has(highlightKey);

            return (
              <ActivityItem
                key={`${activity.type}-${activity.id}`}
                type={activity.type}
                id={activity.id}
                title={activity.title}
                meta={activity.meta}
                snippet={activity.snippet}
                fullContent={activity.fullContent}
                htmlContent={activity.htmlContent}
                timestamp={activity.timestamp}
                isExpanded={expandedIds.has(activity.id)}
                onToggleExpand={() => toggleExpanded(activity.id)}
                urgency={activity.urgency}
                isDone={activity.isDone}
                doneBy={activity.doneBy}
                doneAt={activity.doneAt}
                doneComment={activity.doneComment}
                isHighlighted={isHighlighted}
                isUnread={activity.isUnread}
                author={activity.author}
                authorId={activity.authorId}
                currentUserId={currentUserId}
                assignedTo={activity.assignedTo}
                assignedToId={activity.assignedToId}
                planValidatedByName={activity.planValidatedByName}
                direction={activity.direction}
                recipient={activity.recipient}
                attachments={activity.attachments}
                isEdited={activity.isEdited}
                editedAt={activity.editedAt}
                recordingUrl={activity.recordingUrl}
                durationMs={activity.durationMs}
                voipContactName={activity.voipContactName}
                voipContactRoles={activity.voipContactRoles}
                voipAgentName={activity.voipAgentName}
                voipSummary={activity.voipSummary}
                voipTranscription={activity.voipTranscription}
                voipProcessedAt={activity.voipProcessedAt}
                onMarkDone={
                  onMarkDone
                    ? (id, done, comment) => onMarkDone(activity.type, id, done, comment)
                    : undefined
                }
                onEdit={
                  activity.type === 'tracker_com' && onEdit && activity.fullContent
                    ? () => onEdit(activity.id, activity.fullContent!, activity.urgency, activity.assignedToId)
                    : undefined
                }
                onDelete={
                  activity.type === 'tracker_com' && onDelete
                    ? () => onDelete(activity.id)
                    : undefined
                }
                onViewFull={
                  activity.type === 'email' && onViewEmailFull
                    ? () => onViewEmailFull(activity.id)
                    : undefined
                }
                onReallocate={
                  activity.type === 'email' && onReallocateEmail
                    ? () => onReallocateEmail(activity.id)
                    : activity.type === 'sms_inbox' && onReallocateSms
                      ? () => onReallocateSms(activity.id)
                      : undefined
                }
                onReply={
                  activity.type === 'email' && activity.direction === 'inbound' && onReplyEmail
                    ? () => onReplyEmail(activity.id)
                    : undefined
                }
              />
            );
          })}
        </ActivityDayGroup>
      ))}
    </div>
  );
};

export default ActivityFeed;
